import React from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useThemeStore } from "../../store/themeStore";
import { isDarkTheme } from "../../config/themes";
import useHostDocument from "~/hooks/useHostDocument";

// Floating badge for OTO legs whose price falls outside the rendered ladder
// grid — they have no row to sit on, so their ganho/perda prices are shown
// near the cursor instead. Legs that DO land on a ladder row are drawn in
// place by BookByPriceSuperDOMView (see useBookOrderPreview). Purely
// informational: pointer-events: none, so the shift+click passes through.

const KIND_LABEL = { profit: "Ganho", loss: "Perda", entry: "Entrada" };

const BookOrderPreview = ({ badge }) => {
  const isDarkMode = useThemeStore((s) => isDarkTheme(s.theme));
  // PoC #popout: portal + clamp in the window the ladder actually lives in.
  const [hostDoc, hostProbe] = useHostDocument();
  if (!badge || !badge.items?.length) return hostProbe;
  const win = hostDoc?.defaultView ?? window;

  // Anchor near the cursor, flipping at the viewport edges.
  const W = 210;
  const H = 24 + badge.items.length * 24;
  let left = badge.x + 18;
  let top = badge.y + 18;
  if (left + W > win.innerWidth) left = badge.x - W - 18;
  if (top + H > win.innerHeight) top = badge.y - H - 18;
  left = Math.max(4, left);
  top = Math.max(4, top);

  const portal = createPortal(
    <div
      className={`bbp-order-preview${isDarkMode ? " bbp-order-preview--dark" : ""}`}
      style={{ left, top }}
      data-evo-id="bookbypricepanel-order-preview"
    >
      <div className="bbp-order-preview__head">Fora da escada</div>
      {badge.items.map((item) => (
        <div
          key={item.kind}
          className={`bbp-order-preview__line bbp-order-preview__line--${item.side}`}
        >
          <span className="bbp-order-preview__tag">
            {KIND_LABEL[item.kind] || item.kind}
          </span>
          <span className="bbp-order-preview__price">{item.priceText}</span>
          <span className="bbp-order-preview__dist">{item.distText}</span>
        </div>
      ))}
    </div>,
    (hostDoc ?? document).body,
  );

  return (
    <>
      {hostProbe}
      {portal}
    </>
  );
};

BookOrderPreview.propTypes = {
  badge: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        kind: PropTypes.string,
        priceText: PropTypes.string,
        distText: PropTypes.string,
      }),
    ),
    x: PropTypes.number,
    y: PropTypes.number,
  }),
};

export default React.memo(BookOrderPreview);
